import { useState, useCallback } from 'react';

export interface UseErrorHandlerReturn {
  error: string | null;
  isRetrying: boolean;
  lastFailedMessage: string | null;
  lastFailedAction: 'context' | 'message' | null;
  lastSuccessCallback: (() => void) | null;
  setError: (error: string | null) => void;
  setRetrying: (isRetrying: boolean) => void;
  setFailedMessage: (message: string | null) => void;
  setFailedAction: (action: 'context' | 'message' | null) => void;
  setSuccessCallback: (callback: (() => void) | null) => void;
  clearError: () => void;
}

export function useErrorHandler(): UseErrorHandlerReturn {
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const [lastFailedAction, setLastFailedAction] = useState<'context' | 'message' | null>(null);
  const [lastSuccessCallback, setLastSuccessCallback] = useState<(() => void) | null>(null);

  const setRetrying = useCallback((retrying: boolean) => {
    setIsRetrying(retrying);
  }, []);

  const setFailedMessage = useCallback((message: string | null) => {
    setLastFailedMessage(message);
  }, []);

  const setFailedAction = useCallback((action: 'context' | 'message' | null) => {
    setLastFailedAction(action);
  }, []);

  const setSuccessCallback = useCallback((callback: (() => void) | null) => {
    setLastSuccessCallback(callback);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setIsRetrying(false);
    setLastFailedMessage(null);
    setLastFailedAction(null);
    setLastSuccessCallback(null);
  }, []);

  return {
    error,
    isRetrying,
    lastFailedMessage,
    lastFailedAction,
    lastSuccessCallback,
    setError,
    setRetrying,
    setFailedMessage,
    setFailedAction,
    setSuccessCallback,
    clearError,
  };
}
